import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, ArrowRight, Zap, Database, FileText, 
  Sparkles, CheckCircle2, DollarSign, Activity, Brain,
  Calendar, Users, MessageSquare, Search, Shield, Phone
} from 'lucide-react';

const BackOfficeSuitePage = () => {
  const [activeTab, setActiveTab] = useState('back-office');

  const agents = [
    {
      name: 'Amanda',
      role: 'Insurance Verification',
      description: 'Automatically verifies patient insurance benefits and eligibility hours before visits.',
      image: '/assets/amanda.png',
      link: '/agents/insurance-verification',
      skills: ['Verification', 'Benefits', 'Eligibility']
    },
    {
      name: 'Ariel',
      role: 'Claim Submission',
      description: 'Generates and submits clean insurance claims to payers instantly.',
      image: '/assets/ariel.png',
      link: '/agents/claim-submission',
      skills: ['Claims', 'Submission', 'Coding']
    },
    {
      name: 'Alayna',
      role: 'Claim Scrubbing',
      description: 'Reviews and corrects claims before submission to reduce denials.',
      image: 'https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png',
      skills: ['Auditing', 'Corrections', 'Compliance']
    },
    {
      name: 'Sarah',
      role: 'Denial Management',
      description: 'Handles denied claims, appeals, and resubmissions automatically.',
      image: '/assets/sarah.png',
      skills: ['Appeals', 'Resubmission', 'Negotiation']
    },
    {
      name: 'Kianna',
      role: 'Patient Billing',
      description: 'Manages patient statements and answers billing questions via text/phone.',
      image: '/assets/kianna.png',
      skills: ['Statements', 'Payments', 'Inquiries']
    },
    {
      name: 'Julia',
      role: 'AR & Collections',
      description: 'Tracks accounts receivable and manages collection activities.',
      image: '/assets/julia.png',
      skills: ['Collections', 'Tracking', 'Outreach']
    },
    {
      name: 'Alisha',
      role: 'EOB Posting',
      description: 'Posts explanation of benefits and insurance payments automatically.',
      image: '/assets/alisha.png',
      skills: ['Posting', 'Reconciliation', 'Data Entry']
    },
    {
      name: 'Laura',
      role: 'Insurance Calls',
      description: 'Handles insurance-related phone calls and inquiries efficiently.',
      image: 'https://framerusercontent.com/images/8s85NGEzWU5LVKALaMlopNKSHg.png',
      skills: ['Inbound Calls', 'Inquiries', 'Support']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"></div>
                <span>Complete Back Office Automation</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold leading-tight tracking-tight">
                <span className="text-gray-900">A team of AI agents that can do your</span><br />
                <span className="text-blue-600">billing end-to-end</span>
              </h1>
              
              <p className="text-lg text-gray-700 leading-relaxed max-w-lg">
                Manage your revenue cycle end-to-end. Our full team of AI agents looks at thousands of data points to catch mistakes, increase claim acceptance, and collect more than humans ever could.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <button className="px-6 py-3 rounded-xl font-bold text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-blue-300 transform hover:-translate-y-0.5">
                    Book a Demo
                  </button>
                </Link>
                <button className="px-6 py-3 rounded-xl font-bold text-sm transition-all bg-white text-gray-700 hover:bg-gray-50 border border-gray-200">
                  See Pricing
                </button>
              </div>

            </div>
            
            {/* Right: Interactive Visual - Team Grid */}
            <div className="relative flex items-center justify-center">
              <div className="relative w-full max-w-lg">
                 {/* Decorative background blobs */}
                 <div className="absolute -top-10 -right-10 w-64 h-64 bg-purple-200/40 rounded-full blur-3xl"></div>
                 <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-blue-200/40 rounded-full blur-3xl"></div>

                 {/* Main Grid Card */}
                 <div className="glass-card rounded-3xl p-6 border border-white/60 shadow-xl relative z-10">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">Your Back Office Team</h3>
                            <p className="text-xs text-gray-500">8 Active Agents • 24/7 Coverage</p>
                        </div>
                        <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                            ONLINE
                        </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {agents.map((agent, i) => (
                            <div key={i} className="flex flex-col items-center group cursor-pointer">
                                <div className="relative mb-2">
                                    <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-sm ring-2 ring-white group-hover:ring-blue-400 transition-all transform group-hover:scale-105">
                                        <img src={agent.image} alt={agent.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></div>
                                </div>
                                <span className="text-xs font-bold text-gray-800">{agent.name}</span>
                                <span className="text-[9px] text-gray-500 uppercase tracking-wide text-center leading-tight mt-0.5">{agent.role.split(' ')[0]}</span>
                            </div>
                        ))}
                    </div>

                    {/* Active Action Toast */}
                    <div className="mt-6 bg-white rounded-xl p-3 shadow-md border border-gray-100 flex items-center gap-3 animate-float">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <DollarSign className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-gray-900">Ariel just submitted a claim</p>
                            <p className="text-[10px] text-gray-500 truncate">99% Accuracy Probability</p>
                        </div>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                    </div>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agents Grid Section */}
      <section className="py-24 bg-white relative">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
               Meet Your New <span className="text-blue-600">Back Office Team</span>
            </h2>
            <p className="text-lg text-gray-600">
               Each agent is a specialist in their field, working together to ensure higher accuracy, faster reimbursements, and better collection rates.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {agents.map((agent, index) => (
              <Link 
                to={agent.link || '#'} 
                key={index}
                className={`group relative bg-white rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-900/5 transition-all duration-300 overflow-hidden flex flex-col h-full ${!agent.link ? 'cursor-default' : 'cursor-pointer'}`}
              >
                 <div className="p-5 pb-0 flex items-start justify-between">
                    <div className="relative">
                      <div className="w-32 h-32 rounded-2xl overflow-hidden shadow-sm ring-1 ring-slate-100">
                          <img src={agent.image} alt={agent.name} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-green-500 border-[3px] border-white w-7 h-7 rounded-full" title="Online" />
                    </div>
                    {agent.link && (
                      <div className="w-8 h-8 rounded-full bg-slate-50 group-hover:bg-blue-600 flex items-center justify-center transition-colors duration-300">
                        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                      </div>
                    )}
                  </div>

                  <div className="p-5 flex-1 flex flex-col">
                    <div className="mb-3">
                      <h3 className="text-xl font-bold text-slate-900 mb-0.5">{agent.name}</h3>
                      <p className="text-sm font-bold text-blue-600 uppercase tracking-wide">{agent.role}</p>
                    </div>
                    
                    <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-1">
                      {agent.description}
                    </p>

                    <div className="flex flex-wrap gap-1.5 mt-auto">
                      {agent.skills?.map((skill, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-md bg-slate-50 text-slate-600 text-[11px] font-bold border border-slate-100 uppercase tracking-wider">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="h-1 w-full bg-gradient-to-r from-blue-500 to-indigo-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Two-Card Feature Section (Accuracy & Bandwidth) */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-16 max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    Why Leading Practices Choose <br/>
                    <span className="text-blue-600">Back Office AI</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                    Automating your revenue cycle isn't just about saving time. It's about achieving a level of precision and persistence that is humanly impossible.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Card 1: Greater Accuracy */}
                <div className="flex flex-col">
                    {/* Visual Area */}
                    <div className="bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         {/* Background Orbs */}
                         <div className="absolute top-0 right-0 w-64 h-64 bg-blue-300/30 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
                         <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-300/30 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>
                         
                         {/* Glass Card Container */}
                         <div className="relative w-full max-w-md">
                            {/* Main Active Card */}
                            <div className="glass-card rounded-3xl p-6 relative z-20 bg-white/60 backdrop-blur-xl border border-white/60 shadow-xl">
                                <div className="flex items-start justify-between mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
                                            <FileText className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-lg">Claim #40291</h3>
                                            <p className="text-sm text-blue-600 font-medium">Analysis Complete</p>
                                        </div>
                                    </div>
                                    <div className="bg-green-100 px-3 py-1 rounded-full">
                                        <span className="text-xs font-bold text-green-700 flex items-center gap-1">
                                            <Sparkles className="w-3 h-3" />
                                            99% Accuracy
                                        </span>
                                    </div>
                                </div>

                                {/* Logic Visualization */}
                                <div className="space-y-3">
                                    {/* Check 1 */}
                                    <div className="flex items-center gap-3 animate-float" style={{ animationDelay: '0s' }}>
                                        <div className="bg-white p-2.5 rounded-xl shadow-sm border border-blue-50 flex items-center gap-2 text-xs font-bold text-gray-700 w-full">
                                            <Search className="w-3.5 h-3.5 text-blue-500" />
                                            Prior Denial Check: <span className="text-green-600">Passed</span>
                                        </div>
                                    </div>
                                    
                                    {/* Check 2 */}
                                    <div className="flex items-center gap-3 animate-float" style={{ animationDelay: '1s' }}>
                                        <div className="bg-white p-2.5 rounded-xl shadow-sm border border-purple-50 flex items-center gap-2 text-xs font-bold text-gray-700 w-full">
                                            <Database className="w-3.5 h-3.5 text-purple-500" />
                                            Policy Matching: <span className="text-green-600">D2740 Valid</span>
                                        </div>
                                    </div>

                                    {/* Action */}
                                    <div className="flex justify-end pt-2 animate-float" style={{ animationDelay: '2s' }}>
                                        <div className="bg-blue-600 text-white px-4 py-2 rounded-xl shadow-lg shadow-blue-200 flex items-center gap-2 text-sm font-bold transform scale-105">
                                            <CheckCircle className="w-4 h-4" />
                                            Safe to Submit
                                        </div>
                                    </div>
                                </div>
                            </div>
                         </div>
                    </div>

                    {/* Text Content */}
                    <div>
                        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Search className="w-4 h-4" />
                             INTELLIGENT ANALYSIS
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">Greater Accuracy</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Our agents look at thousands of data points and prior denial reasons to catch mistakes that would be impossible for humans to catch. This gets your claim acceptance rate up instantly.
                        </p>
                    </div>
                </div>

                {/* Card 2: Increased Bandwidth */}
                <div className="flex flex-col">
                    {/* Visual Area */}
                    <div className="bg-gradient-to-br from-indigo-100 via-purple-100 to-indigo-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         {/* Background Orbs */}
                         <div className="absolute top-10 left-10 w-40 h-40 bg-purple-300/30 rounded-full blur-3xl"></div>
                         <div className="absolute bottom-10 right-10 w-40 h-40 bg-blue-300/30 rounded-full blur-3xl"></div>
                         
                        <div className="relative w-full max-w-md z-10">
                            {/* Active Tasks Effect */}
                            <div className="space-y-4">
                                {/* Task 1 */}
                                <div className="glass-card bg-white/80 p-4 rounded-2xl border border-white/60 shadow-lg transform translate-x-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                            <Phone className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs font-bold text-gray-900">Calling Delta Dental</p>
                                            <p className="text-[10px] text-gray-500">Appeal for Claim #9921</p>
                                        </div>
                                        <div className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</div>
                                    </div>
                                </div>

                                {/* Task 2 */}
                                <div className="glass-card bg-white/90 p-4 rounded-2xl border border-white/60 shadow-xl z-10">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                                            <MessageSquare className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs font-bold text-gray-900">Texting Sarah J.</p>
                                            <p className="text-[10px] text-gray-500">Patient Balance: $45.00</p>
                                        </div>
                                        <div className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</div>
                                    </div>
                                </div>

                                {/* Task 3 */}
                                <div className="glass-card bg-white/80 p-4 rounded-2xl border border-white/60 shadow-lg transform translate-x-8">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                            <Shield className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs font-bold text-gray-900">Verifying Insurance</p>
                                            <p className="text-[10px] text-gray-500">For tomorrow's 12 patients</p>
                                        </div>
                                        <div className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Text Content */}
                    <div>
                        <div className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Zap className="w-4 h-4" />
                             UNLIMITED SCALE
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">Increased Bandwidth</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Humans are strapped for bandwidth; AI is not. Whether it's following up on denials, verifying insurance multiple times, or communicating with patients to collect balances, our agents never stop.
                        </p>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* Deep PMS Integration Section */}
      <section className="py-24 px-6 relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
            <div className="grid md:grid-cols-2 gap-16 items-center">
                <div>
                   <div className="inline-block bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-6">
                      SEAMLESS MIGRATION
                   </div>
                   <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
                      Seamless PMS Migration + <br/> Revenue Cycle Suite
                   </h2>
                   <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                      We migrate ledger, claims, and patient history into Asha so billing and collections run inside one system.
                   </p>
                   
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700 hover:bg-slate-800 transition-colors">
                          <Database className="w-8 h-8 text-blue-400 mb-4" />
                          <h4 className="font-bold text-lg mb-2">Native Asha Ledger</h4>
                          <p className="text-sm text-slate-400">Patient balances and claims status live in Asha after migration.</p>
                      </div>
                      <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700 hover:bg-slate-800 transition-colors">
                          <Activity className="w-8 h-8 text-purple-400 mb-4" />
                          <h4 className="font-bold text-lg mb-2">Claim Analytics</h4>
                          <p className="text-sm text-slate-400">Real-time insights into acceptance rates and aging AR.</p>
                      </div>
                   </div>
                </div>

                <div className="bg-slate-800 rounded-3xl p-8 border border-slate-700 shadow-2xl relative">
                    <div className="absolute top-4 right-4 flex gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500/50"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500/50"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500/50"></div>
                    </div>
                    
                    <div className="mt-4 space-y-6">
                        {/* PMS Logos Row */}
                        <div className="grid grid-cols-2 gap-4">
                            {['Dentrix', 'Eaglesoft', 'Open Dental', 'Curve'].map((pms, i) => (
                                <div key={i} className="bg-slate-900/50 h-12 rounded-lg flex items-center justify-center border border-slate-700/50 text-slate-500 font-bold text-sm">
                                    {pms}
                                </div>
                            ))}
                        </div>
                        
                        {/* Connection Visual */}
                        <div className="relative h-20 flex items-center justify-center">
                            <div className="w-full h-px bg-slate-700 absolute"></div>
                            <div className="bg-blue-600 text-white px-4 py-1.5 rounded-full text-xs font-bold relative z-10 flex items-center gap-2 shadow-lg shadow-blue-900/50">
                                <Zap className="w-3 h-3 fill-current" />
                                MIGRATION COMPLETE
                            </div>
                        </div>

                        {/* Asha Suite */}
                        <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                                    <Sparkles className="w-4 h-4 text-white" />
                                </div>
                                <span className="font-bold">Asha Revenue Dashboard</span>
                            </div>
                            <div className="space-y-2">
                                <div className="h-2 bg-slate-800 rounded-full w-3/4"></div>
                                <div className="h-2 bg-slate-800 rounded-full w-1/2"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BackOfficeSuitePage;






























